import { SortOrder } from '@/enums/SortOrder';
import qs from 'qs';
import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';

const initialState: CollectionState = {
  all: [],
  viewing: undefined,
};

const getters: GetterTree<CollectionState, RootState> = {
  all(state: CollectionState): Collection[] {
    return state.all;
  },
  viewing(state: CollectionState): Collection | undefined {
    return state.viewing;
  },
};

const mutations: MutationTree<CollectionState> = {
  RESET(state: CollectionState) {
    state.all = [];
    state.viewing = undefined;
  },
  ADD_MODEL(state: CollectionState, payload: Collection) {
    state.all.push(payload);
  },
  ADD_MODELS(state: CollectionState, payload: Collection[]) {
    state.all = [...state.all, ...payload];
  },
  SET_MODELS(state: CollectionState, payload: Collection[]) {
    state.all = payload;
  },
  UPDATE_MODEL(state: CollectionState, payload: Collection) {
    const index = state.all.findIndex((model: Collection) => model.id === payload.id);

    if (index === -1) {
      state.all.push(payload);
    } else {
      state.all[index] = payload;
    }
  },
  REMOVE_MODEL(state: CollectionState, payload: number) {
    state.all = state.all.filter((model: Collection) => model.id !== payload);
  },
  SET_VIEWING(state: CollectionState, payload: Collection) {
    state.viewing = payload;
  },
};

const resource: string = 'collections';

const actions: ActionTree<CollectionState, RootState> = {
  clearViewing({ commit }) {
    commit('SET_VIEWING', undefined);
  },
  create({ commit, rootState }, { data, workspaceId }: { data: CollectionData; workspaceId: number }) {
    return rootState.api
      .post(`${resource}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collection }) => {
        commit('ADD_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  index(
    { commit, rootState },
    { query = { s: { created_at: SortOrder.DESC, id: SortOrder.ASC }, per_page: 20 }, workspaceId }: { query?: IndexQuery<Collection & { products: number[] }>; workspaceId: number },
  ) {
    const queryString = qs.stringify(query, { encode: false });

    return rootState.api
      .get(`${resource}?${queryString}`, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collection }) => {
        if (query?.hasOwnProperty('after')) {
          commit('ADD_MODELS', response.data);
        } else {
          commit('SET_MODELS', response.data);
        }

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  read({ commit, rootState }, payload: { id: number; workspaceId: number }) {
    return rootState.api
      .get(`${resource}/${payload.id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': payload.workspaceId } } })
      .then((response: { data: Collection }) => {
        commit('UPDATE_MODEL', response.data);

        commit('SET_VIEWING', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  update({ commit, rootState }, { id, data, workspaceId }: { id: number; data: CollectionData; workspaceId: number }) {
    return rootState.api
      .put(`${resource}/${id}`, data, { withCredentials: true, headers: { common: { 'x-workspace-id': workspaceId } } })
      .then((response: { data: Collection }) => {
        commit('UPDATE_MODEL', response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  delete({ commit, rootState }, payload: { id: number; workspaceId: number }) {
    return rootState.api
      .delete(`${resource}/${payload.id}`, { withCredentials: true, headers: { common: { 'x-workspace-id': payload.workspaceId } } })
      .then((response: { data: Collection }) => {
        commit('REMOVE_MODEL', payload.id);

        return Promise.resolve(payload.id);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
